import '@fontsource/open-sans';

import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType, useRoutes } from 'react-router-dom';
import { routesConfig } from './app/routes-config';
import './styles/antd-overrides.css';
import './styles/entry.css';
import useLocalizeDocumentAttributes from './shared/hooks/use-localize-document-attributes';

if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    release: process.env.REACT_APP_VERSION,
    environment: process.env.REACT_APP_ENVIRONMENT,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: 1.0,
  });
}

const AppHelmet = (): JSX.Element => {
  return <Helmet titleTemplate="%s | Sign on Tab Portal" defaultTitle="Sign on Tab Portal" />;
};

const App = (): JSX.Element => {
  const routesElement = useRoutes(routesConfig);
  useLocalizeDocumentAttributes();

  return (
    <>
      <AppHelmet />
      {routesElement}
    </>
  );
};

const useSentryRoutes = Sentry.wrapUseRoutesV7(useRoutes);

const AppWithSentry = (): JSX.Element => {
  const routesElement = useSentryRoutes(routesConfig);

  return (
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
      <AppHelmet />
      {routesElement}
    </Sentry.ErrorBoundary>
  );
};

export default process.env.REACT_APP_SENTRY_DSN ? AppWithSentry : App;
